import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import { FileDropperContextProvider } from 'lib/form/file-dropper/file-dropper.context';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { useFindComponentDimensions } from '../component.hooks';
import { SimpleModalVerticalActionsStyled } from 'lib/modal/simple-modal.styles';
import { PrimaryButton } from 'approot/shared/styles/buttons/primary-button';
import { CONTENT_IMAGE } from 'global/constants';
import { useImageRequestLoader } from 'approot/shared/images/images.hooks';
import { MoreInfo } from '../shared/more-information/more-information-toolip';
import TextInputLimit from 'approot/shared/text-input-limit/text-input-limit';
import { GENERATE_IMAGE_ALT_TEXT } from 'approot/dictionary/dictionary.graphql';
import type {
  GenerateImageAltTextQuery,
  GenerateImageAltTextQueryVariables,
} from '../../../dictionary/dictionary.graphql.generated';
import { useLoggingLazyQuery } from 'lib/apollo/apollo-hooks';
import { SecondaryLoadingButton } from 'approot/shared/styles/buttons/loading-button';
import ImageUploadField, { ImageProps } from '../shared/image-upload-field';
import { Box } from 'approot/shared/design-system/box/box.style';
import { PStyled } from 'approot/shared/styles/typography.style';
import { useUpdateComponent } from 'approot/activity-builder/activity-builder.hooks';
import ComponentImageSource from '../shared/component-image-modal/component-image-source';
import ComponentAltTextTooltip from '../shared/component-alt-text/component-alt-text-tooltip';
import { EditProps } from './image-sphere.constants';
import { ALT_TEXT_MAX } from '../shared/component-alt-text/component-alt-text.constants';

const IMAGE_KEY = 'sphereImageKey';
const PRINT_IMAGE_KEY = 'printImageKey';
const ALT_TEXT_FIELD = 'altText';
const SOURCE_URL = 'sourceUrl';

type FormData = {
  [IMAGE_KEY]?: string | null;
  [PRINT_IMAGE_KEY]?: string | null;
  [ALT_TEXT_FIELD]?: string | null;
  [SOURCE_URL]?: string;
};

type Props = EditProps & {
  fileToUpload?: {
    key: string;
    fileName: string;
    file: File;
  };
  onClose: () => void;
};

const ImageSphereEditModal = ({
  id,
  sectionId,
  properties,
  fileToUpload,
  onClose,
}: Props) => {
  const [imageData, setImageData] = useState<
    | (ImageProps & {
        file?: File;
      })
    | null
  >(fileToUpload ? fileToUpload : properties.image);

  const [printImageData, setPrintImageData] = useState<ImageProps | null>(
    properties.printImage
  );
  const [sourceUrl, setSourceUrl] = useState<string>(
    properties.sourceUrl || ''
  );

  const { elementDimensionLabel } = useFindComponentDimensions(id);

  const { updateComponent, loading } = useUpdateComponent({
    onCompleted: () => onClose(),
  });

  const [generateAltText, { loading: loadingAltText }] = useLoggingLazyQuery<
    GenerateImageAltTextQuery,
    GenerateImageAltTextQueryVariables
  >(GENERATE_IMAGE_ALT_TEXT, {
    fetchPolicy: 'no-cache',
    onCompleted: res => {
      const altText = res.generateImageAltText.altText.substring(
        0,
        ALT_TEXT_MAX
      );
      setValue(ALT_TEXT_FIELD, altText);
      const altTextElement = document.getElementById(ALT_TEXT_FIELD);
      altTextElement?.focus();
    },
  });

  const formMethods = useForm<FormData>({
    defaultValues: {
      [IMAGE_KEY]: imageData?.key,
      [PRINT_IMAGE_KEY]: properties.printImage?.key,
      [ALT_TEXT_FIELD]: properties.altText,
    },
  });
  const {
    formState: { errors },
    register,
    handleSubmit,
    setValue,
  } = formMethods;

  const { getImageResponsive } = useImageRequestLoader({
    url: properties.image?.original,
    onLoad: () => false,
  });

  const {
    imageUrl: printImageUrl,
    getImageResponsive: getPrintImageResponsive,
    setImageUrl: setPrintImageUrl,
  } = useImageRequestLoader({
    url: properties.printImage?.original,
    onLoad: () => false,
  });

  const handleFileChange = useCallback(
    (key: string, fileName: string) => {
      getImageResponsive({
        variables: {
          input: {
            key: key,
            imageType: CONTENT_IMAGE,
          },
        },
      }).then(res => {
        setImageData({
          ...imageData,
          key,
          fileName,
          original: res.data?.getImageResponsive.original,
        });
      });

      setValue(IMAGE_KEY, key, { shouldValidate: true });
    },
    [imageData, setImageData, setValue, getImageResponsive]
  );

  const handlePrintFileChange = useCallback(
    (key: string, fileName: string) => {
      getPrintImageResponsive({
        variables: {
          input: {
            key: key,
            imageType: CONTENT_IMAGE,
          },
        },
      }).then(res => {
        setPrintImageData({
          ...printImageData,
          key,
          fileName,
          original: res.data?.getImageResponsive.original,
        });
      });
      setValue(PRINT_IMAGE_KEY, key, { shouldValidate: true });
    },
    [printImageData, setPrintImageData, setValue, getPrintImageResponsive]
  );

  const handleDeleteImage = useCallback(() => {
    setImageData(null);
    setValue(IMAGE_KEY, undefined);
  }, [setImageData, setValue]);

  const handleDeletePrintImage = useCallback(() => {
    setPrintImageData(null);
    setValue(PRINT_IMAGE_KEY, undefined);
    setPrintImageUrl(undefined);
  }, [setPrintImageUrl, setValue]);

  const onSubmit = useCallback(
    (data: FormData) => {
      if (!imageData || !printImageData) return;

      updateComponent({
        componentId: id,
        sectionId,
        componentData: {
          properties: {
            image: {
              key: imageData.key,
              imageType: CONTENT_IMAGE,
              fileName: imageData.fileName,
            },
            printImage: {
              key: printImageData.key,
              imageType: CONTENT_IMAGE,
              fileName: printImageData.fileName,
            },
            altText: data[ALT_TEXT_FIELD],
            sourceUrl: sourceUrl,
          },
        },
      });
    },
    [id, sectionId, imageData, printImageData, sourceUrl, updateComponent]
  );

  useEffect(() => {
    register(IMAGE_KEY, {
      required: 'Oops! It looks like you haven’t chosen a 360 image File.',
    });
    register(PRINT_IMAGE_KEY, {
      required: 'Oops! It looks like you haven’t set an image for print.',
    });
  }, [register]);

  return (
    <ActivityBuilderModal
      isVisible
      title="Edit 360° Image"
      headerTitle="Edit 360° Image"
      size={500}
      onClose={onClose}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SettingsGroup>
            <SettingsLabel>
              360° Image Upload{' '}
              <MoreInfo>
                Please upload a special 360-degree image file that allows
                viewers to look in all directions—ensure the file is in a wide,
                panoramic format to avoid issues with incorrect uploads.
              </MoreInfo>
            </SettingsLabel>
            <ImageUploadField
              fieldName={IMAGE_KEY}
              image={imageData ? imageData : null}
              elementDimensionLabel={elementDimensionLabel}
              error={errors[IMAGE_KEY]?.message}
              onImageUpload={handleFileChange}
              onDelete={handleDeleteImage}
            />
          </SettingsGroup>
          <SettingsGroup>
            <SettingsLabel>Static Image for Print</SettingsLabel>
            <ImageUploadField
              fieldName={PRINT_IMAGE_KEY}
              image={printImageData ? printImageData : null}
              elementDimensionLabel={elementDimensionLabel}
              error={errors[PRINT_IMAGE_KEY]?.message}
              onImageUpload={handlePrintFileChange}
              onDelete={handleDeletePrintImage}
            />
          </SettingsGroup>
          <ComponentImageSource sourceUrl={sourceUrl} onChange={setSourceUrl} />
          <SettingsGroup marginBottom={0}>
            <Box $justifyContent="space-between">
              <SettingsLabel htmlFor={ALT_TEXT_FIELD}>
                Alt Text
                <ComponentAltTextTooltip />
              </SettingsLabel>
              <PStyled marginBottom={0}>Optional</PStyled>
            </Box>

            <TextInputLimit
              rows={3}
              maxLength={ALT_TEXT_MAX}
              id={ALT_TEXT_FIELD}
              name={ALT_TEXT_FIELD}
              placeholder={
                loadingAltText
                  ? 'Generating Alt Text...'
                  : 'Describe this image'
              }
              defaultValue={properties.altText || ''}
              isDisabled={loadingAltText || loading}
            />
          </SettingsGroup>
          <SimpleModalVerticalActionsStyled>
            <SecondaryLoadingButton
              data-selector="generate-alt-text"
              isLoading={loadingAltText}
              isDisabled={loadingAltText || loading || !printImageUrl}
              aria-disabled={loadingAltText || loading || !printImageUrl}
              onClick={() =>
                generateAltText({
                  variables: {
                    input: {
                      imageUrl: printImageUrl || '',
                    },
                  },
                })
              }
            >
              {loadingAltText ? 'Generating Alt Text' : 'Generate Alt Text ✨'}
            </SecondaryLoadingButton>
            <PrimaryButton
              type="submit"
              $isDisabled={loading}
              loading={loading}
            >
              Save
            </PrimaryButton>
          </SimpleModalVerticalActionsStyled>
        </form>
      </FormProvider>
    </ActivityBuilderModal>
  );
};

const ImageSphereEditModalWrapper = (props: Props) => (
  <FileDropperContextProvider>
    <ImageSphereEditModal {...props} />
  </FileDropperContextProvider>
);

export default ImageSphereEditModalWrapper;
